import React from 'react';
import PropTypes from 'prop-types';
import { Loader, Message } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';

import MfaSetupSteps from '../../../Settings/account/MFASettings/MfaSetupSteps';
import BackupCodes from '../../../Settings/account/MFASettings/BackupCodes';
import { useOnboardingMfa, ONBOARDING_MFA_STEP } from './useOnboardingMfa';

// Constrain + center the step via a block Box with top-level maxWidth/mx — rebass applies
// those as real CSS, whereas the `sx` form silently no-ops in this layout. Self-constraining
// means callers (walkthrough, ally invite, shared-list card) don't each have to wrap it to
// avoid a full-width sprawl. `topGap` separates it from the stepper above it in the
// walkthrough; callers whose own layout already supplies top spacing pass "0px".
const columnSx = { gap: '24px', flexDirection: 'column' };

export const OnboardingMfa = ({ network, onComplete, topGap }) => {
  const containerProps = { maxWidth: '432px', width: '100%', mx: 'auto', mt: topGap };
  const { step, setupData, backupCodes, loadError, enrollCode, finish } = useOnboardingMfa({ onComplete });

  if (step === ONBOARDING_MFA_STEP.BACKUP_CODES) {
    return (
      <Box {...containerProps}>
        <Flex sx={columnSx}>
          <Text as="h1" fontSize="22px" fontWeight="600" lineHeight="1.2">
            Save your backup codes
          </Text>
          <BackupCodes codes={backupCodes} onDone={finish} fullWidth />
        </Flex>
      </Box>
    );
  }

  return (
    <Box {...containerProps}>
      <Flex sx={columnSx}>
        <Text as="h1" fontSize="22px" fontWeight="600" lineHeight="1.2">
          Set up two-factor authentication
        </Text>
        <Text fontSize="14px" color="text.main">
          {network?.name} requires two-factor authentication to finish setting up your account.
        </Text>
        {loadError && <Message type="error" content="Could not start two-factor setup. Please try again." />}
        {!setupData && !loadError && (
          <Flex justifyContent="center" py="24px">
            <Loader />
          </Flex>
        )}
        {setupData && (
          <MfaSetupSteps setupData={setupData} onSubmit={enrollCode} dataCyPrefix="onboarding-mfa-enroll" />
        )}
      </Flex>
    </Box>
  );
};

OnboardingMfa.propTypes = {
  network: PropTypes.object,
  onComplete: PropTypes.func,
  topGap: PropTypes.string,
};

OnboardingMfa.defaultProps = {
  network: null,
  onComplete: undefined,
  topGap: '100px',
};
